// src/pages/LoginPage.tsx
// PRC-017 · A4: iniciar sesión, con aviso de email sin confirmar y errores en español.
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { supabase } from '../config/supabaseClient';
import queryClient from '../config/queryClient';
import { AuthSplitLayout } from '../modules/pricingJourney/components/JourneyLayouts';
import { JourneyField, Notice } from '../modules/pricingJourney/components/JourneyBlocks';
import PasswordField from '../modules/pricingJourney/components/PasswordField';
import { Eyebrow, JourneyButton, JourneyText } from '../modules/pricingJourney/components/JourneyBasics';
import { J } from '../modules/pricingJourney/journeyTokens';
import { EMAIL_RE, focusFirstError, OFFLINE_MESSAGE, resendConfirmation } from '../modules/pricingJourney/journeyAuth';

// Página que el usuario quería abrir (la deja ProtectedRoute). Las rutas
// protegidas siguen repartiendo por rol (operario, responsable, Pricing).
const targetFrom = (state: unknown): string => {
  const from = (state as { from?: { pathname?: string; search?: string; hash?: string } } | null)?.from;
  if (!from?.pathname || !from.pathname.startsWith('/') || from.pathname.startsWith('//') || from.pathname === '/login') return '/';
  return `${from.pathname}${from.search ?? ''}${from.hash ?? ''}`;
};

const LoginPage: React.FC = () => {
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  // PRC-030: errores de cada campo, validados al salir de él y al enviar.
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [unconfirmed, setUnconfirmed] = useState<string | null>(null);
  const [resendMsg, setResendMsg] = useState<{ tone: 'ok' | 'warn' | 'danger'; text: string } | null>(null);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setUnconfirmed(null);
    setResendMsg(null);
    const nextEmailError = !email.trim() ? 'Escribe tu email.' : !EMAIL_RE.test(email.trim()) ? 'Este email no parece correcto.' : null;
    const nextPasswordError = !password ? 'Escribe tu contraseña.' : null;
    setEmailError(nextEmailError);
    setPasswordError(nextPasswordError);
    if (nextEmailError || nextPasswordError) {
      focusFirstError([[nextEmailError, 'a4-email'], [nextPasswordError, 'a4-pass']]);
      return;
    }
    setLoading(true);
    try {
      // IND-058: se vacía antes de entrar: después cancelaría lo que la sesión
      // nueva ya está pidiendo (p. ej. el rol industrial del operario).
      queryClient.clear();
      const { error: signInError } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      if (signInError) throw signInError;
      window.location.href = targetFrom(location.state);
    } catch (err: any) {
      const code = String(err?.code || '').toLowerCase();
      const message = String(err?.message || '').toLowerCase();
      if (code === 'email_not_confirmed' || message.includes('email not confirmed')) {
        setUnconfirmed(email.trim());
      } else if (code === 'invalid_credentials' || message.includes('invalid login credentials')) {
        setError('El email o la contraseña no son correctos.');
      } else if (err?.status === 429 || code.includes('rate_limit') || message.includes('rate limit') || message.includes('too many')) {
        setError('Has hecho demasiados intentos. Espera unos minutos y vuelve a probar.');
      } else if (err?.name === 'AuthRetryableFetchError' || message.includes('fetch') || message.includes('network')) {
        setError(OFFLINE_MESSAGE);
      } else {
        setError('No hemos podido iniciar sesión. Inténtalo de nuevo en unos minutos.');
      }
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (!unconfirmed) return;
    const r = await resendConfirmation(unconfirmed);
    setResendMsg(r === 'sent'
      ? { tone: 'ok', text: 'Te hemos enviado un enlace nuevo. Revisa tu correo.' }
      : r === 'limited'
        ? { tone: 'warn', text: 'Acabamos de enviarte uno. Podrás pedir otro dentro de un minuto.' }
        : { tone: 'danger', text: r === 'offline' ? OFFLINE_MESSAGE : 'No hemos podido reenviar el enlace. Inténtalo en unos minutos.' });
  };

  const linkSx = { color: J.skyText, fontWeight: 600, textDecoration: 'none' };

  return (
    <AuthSplitLayout eyebrow="Margin Center · Control comercial · Optimización">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <Eyebrow tone="sky">Acceso</Eyebrow>
        <JourneyText variant="title">Inicia sesión</JourneyText>
        <JourneyText variant="lead">Entra con el email de tu empresa.</JourneyText>
      </Box>
      {unconfirmed && (
        <Notice tone="warn">
          Aún no has confirmado <b>{unconfirmed}</b>. Abre el enlace que te enviamos al crear la cuenta.{' '}
          <Box component="button" type="button" onClick={resend} sx={{ all: 'unset', cursor: 'pointer', ...linkSx }}>Reenviar enlace de confirmación</Box>
        </Notice>
      )}
      {resendMsg && <Notice tone={resendMsg.tone}>{resendMsg.text}</Notice>}
      <Box component="form" noValidate onSubmit={handleLogin} sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <JourneyField
          id="a4-email" label="Email" type="email" value={email} autoComplete="email" autoFocus error={emailError}
          onChange={(e) => { setEmail(e.target.value); setEmailError(null); }}
          onBlur={() => setEmailError(email.trim() && !EMAIL_RE.test(email.trim()) ? 'Este email no parece correcto.' : null)}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <PasswordField
            id="a4-pass" label="Contraseña" value={password} autoComplete="current-password" error={passwordError}
            onChange={(e) => { setPassword(e.target.value); setPasswordError(null); }}
          />
          <Box component="a" href="/recuperar-contrasena" sx={{ ...linkSx, fontSize: 13, alignSelf: 'flex-end' }}>¿Has olvidado la contraseña?</Box>
        </Box>
        {error && <Notice tone="danger">{error}</Notice>}
        <JourneyButton type="submit" block disabled={loading}>{loading ? 'Entrando…' : 'Entrar'}</JourneyButton>
      </Box>
      <JourneyText variant="small" sx={{ textAlign: 'center', '& a': linkSx }}>
        ¿No tienes cuenta? <a href="/signup">Empieza tu prueba gratuita</a>
      </JourneyText>
    </AuthSplitLayout>
  );
};

export default LoginPage;
